import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Typography,
    Box,
    Paper,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Container,
    AppBar,
    Toolbar,
    Chip,
    Avatar
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import apiService from '../services/api';
import ChartRenderer from '../components/ChartRenderer';
import logo from '../assets/logo.png';

const SharedChat = () => {
    const { chatId } = useParams();
    const [chat, setChat] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchSharedChat = async () => {
            try {
                setLoading(true);
                setError('');
                
                const response = await apiService.getSharedChat(chatId);
                console.log('Shared chat response:', response.data);
                
                if (response.data.messages) {
                    setChat(response.data);
                } else {
                    setError('Chat data not available');
                }
            } catch (err) {
                console.error('Error loading shared chat:', err);
                if (err.response?.status === 404) {
                    setError('Chat not found or not available for sharing');
                } else {
                    setError('Failed to load chat. Please check the link and try again.');
                }
            } finally {
                setLoading(false);
            }
        };

        if (chatId) {
            fetchSharedChat();
        }
    }, [chatId]);

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    // Function to extract and render chart data from message content
    const renderMessageContent = (content) => {
        // Check if content contains JSON data that might be chart data
        const jsonPattern = /```json\s*(\{[\s\S]*?\})\s*```/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = jsonPattern.exec(content)) !== null) {
            // Add text before the JSON
            if (match.index > lastIndex) {
                parts.push({
                    type: 'text',
                    content: content.substring(lastIndex, match.index)
                });
            }

            // Try to parse and render the JSON as a chart
            try {
                const jsonData = JSON.parse(match[1]);
                if (jsonData.type && (jsonData.type === 'bar' || jsonData.type === 'line' || jsonData.type === 'pie' || jsonData.type === 'radar')) {
                    parts.push({
                        type: 'chart',
                        data: jsonData
                    });
                } else {
                    // If it's not chart data, render as code block
                    parts.push({
                        type: 'text',
                        content: match[0]
                    });
                }
            } catch (e) {
                // If JSON parsing fails, render as code block
                parts.push({
                    type: 'text',
                    content: match[0]
                });
            }

            lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < content.length) {
            parts.push({
                type: 'text',
                content: content.substring(lastIndex)
            });
        }

        // If no JSON patterns found, return the whole content as text
        if (parts.length === 0) {
            parts.push({
                type: 'text',
                content: content
            });
        }

        return parts.map((part, index) => (
            <Box key={index}>
                {part.type === 'text' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {part.content}
                    </ReactMarkdown>
                ) : (
                    <Box sx={{ my: 2 }}>
                        <ChartRenderer visualization={part.data} />
                    </Box>
                )}
            </Box>
        ));
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <AppBar position="static" sx={{ bgcolor: '#5b1256' }}>
                    <Toolbar>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img src={logo} alt="BrandSpark" style={{ height: '32px', marginRight: '16px' }} />
                            <Typography variant="h6" component="div">
                                BrandSpark ® Trust Survey - Shared Chat
                            </Typography>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CircularProgress size={60} />
                        <Typography variant="h6" sx={{ mt: 2 }}>
                            Loading chat...
                        </Typography>
                    </Box>
                </Container>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <AppBar position="static" sx={{ bgcolor: '#5b1256' }}>
                    <Toolbar>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img src={logo} alt="BrandSpark" style={{ height: '32px', marginRight: '16px' }} />
                            <Typography variant="h6" component="div">
                                BrandSpark ® Trust Survey - Shared Chat
                            </Typography>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
                    <Alert severity="error" sx={{ mt: 4 }}>
                        {error}
                    </Alert>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* Header */}
            <AppBar position="static" sx={{ bgcolor: '#5b1256' }}>
                <Toolbar>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img src={logo} alt="BrandSpark" style={{ height: '32px', marginRight: '16px' }} />
                        <Typography variant="h6" component="div">
                            BrandSpark ® Trust Survey - Shared Chat
                        </Typography>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Main Content */}
            <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
                {chat && (
                    <>
                        {/* Chat Header */}
                        <Card sx={{ mb: 4 }}>
                            <CardContent>
                                <Typography variant="h4" component="h1" gutterBottom align="center">
                                    {chat.title || 'Shared Chat'}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                                    <Chip label={`Country: ${chat.country}`} variant="outlined" />
                                    <Chip 
                                        label={`Created: ${new Date(chat.created_at).toLocaleDateString()}`} 
                                        variant="outlined" 
                                    />
                                    <Chip 
                                        label={`${chat.messages.length} messages`} 
                                        variant="outlined" 
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Chat Messages */}
                        <Box sx={{ mb: 4 }}>
                            {chat.messages.map((message, index) => (
                                <Box key={index} sx={{ mb: 3 }}>
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            p: 2,
                                            backgroundColor: message.role === 'user' ? '#f3f4f6' : '#ffffff',
                                            border: message.role === 'user' ? '1px solid #e5e7eb' : '1px solid #d1d5db'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                            <Avatar
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    bgcolor: message.role === 'user' ? '#6b7280' : '#5b1256'
                                                }}
                                            >
                                                {message.role === 'user' ? <PersonIcon /> : <SmartToyIcon />}
                                            </Avatar>
                                            
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: message.role === 'user' ? '#374151' : '#5b1256'
                                                        }}
                                                    >
                                                        {message.role === 'user' ? 'User' : 'Assistant'}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ ml: 2, color: 'text.secondary' }}
                                                    >
                                                        {formatTimestamp(message.timestamp)}
                                                    </Typography>
                                                </Box>
                                                
                                                <Box sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                                                    {message.role === 'assistant' ? (
                                                        renderMessageContent(message.content)
                                                    ) : (
                                                        <Typography variant="body1">
                                                            {message.content}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Box>
                            ))}
                        </Box>

                        {/* Contact Info */}
                        <Box sx={{ mt: 6, py: 3, px: 2, borderTop: '1px solid #eee', textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                To learn more about the BrandSpark Most Trusted Awards program, methodology, or for a full list of winning brands, visit{' '}
                                <a href="https://www.brandsparkmosttrusted.com" target="_blank" rel="noopener noreferrer">
                                    www.brandsparkmosttrusted.com
                                </a>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Contact: Robert Levy, BrandSpark{' '}
                                <a href="mailto:RLevy@BrandSpark.com">RLevy@BrandSpark.com</a>
                            </Typography>
                        </Box>

                        {/* Footer */}
                        <Box sx={{ mt: 2, py: 3, textAlign: 'center', borderTop: '1px solid #eee' }}>
                            <Typography variant="body2" color="text.secondary">
                                © 2025 BrandSpark International Inc. All Rights Reserved
                            </Typography>
                        </Box>
                    </>
                )}
            </Container>
        </Box>
    );
};

export default SharedChat; 